import { h } from "preact";
import { useState, useEffect } from "preact/hooks";
import useFormStore from "../../store/useFormStore";
import { parseLocalDate } from "../../util/helper";
import DatePicker from "react-datepicker";
import { locationService } from "../../api/client";
import "react-datepicker/dist/react-datepicker.css";
import ServicesCheckBoxList from "./ServicesCheckBoxList";

export default function ShortLeadFormFields() {
  const shortLeadFormPreferredDate = useFormStore(
    (state) => state.shortLeadFormPreferredDate,
  );
  const shortLeadFormNote = useFormStore((state) => state.shortLeadFormNote);
  const setShortLeadFormPreferredDate = useFormStore(
    (state) => state.setShortLeadFormPreferredDate,
  );
  const setShortLeadFormNote = useFormStore(
    (state) => state.setShortLeadFormNote,
  );
  const currentFranchise = useFormStore((state) => state.currentFranchise);

  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(true);

  // Fetch holidays when component mounts
  useEffect(() => {
    const fetchHolidays = async () => {
      if (!currentFranchise?.id) {
        setHolidaysLoading(false);
        return;
      }

      try {
        setHolidaysLoading(true);
        const response = await locationService.getHolidays(currentFranchise.id);
        if (response.success && response.holidays) {
          // Filter out empty objects with no start date.
          setHolidays(response.holidays.filter((h) => h.start));
        }
      } catch (error) {
        console.error("Failed to fetch holidays:", error);
      } finally {
        setHolidaysLoading(false);
      }
    };

    fetchHolidays();
  }, [currentFranchise?.id]);

  // Calculate minimum date based on days_out__quote (default 3)
  const getMinDate = () => {
    const daysOut = currentFranchise?.days_out__quote || 3;
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + daysOut);
    return minDate;
  };

  const formatLocalDate = (date) =>
    [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0"),
    ].join("-");

  // Convert holidays to date ranges for excludeDateIntervals
  const getExcludedDateIntervals = () => {
    return holidays
      .map((holiday) => ({
        start: parseLocalDate(holiday.start),
        end: parseLocalDate(holiday.end || holiday.start, true),
      }))
      .filter((interval) => interval.start && interval.end);
  };

  // Filter dates based on serviced_days
  const filterDate = (date) => {
    if (!currentFranchise?.serviced_days) {
      return true;
    }

    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    return currentFranchise.serviced_days.includes(dayOfWeek.toString());
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <label
          htmlFor="preferredDate"
          className="block text-lg font-semibold mb-2"
        >
          Preferred Date
          <span className="text-coit-error ml-1">*</span>
        </label>
        {holidaysLoading ? (
          <div className="w-full max-w-md px-4 py-3 border-2 border-gray-300 rounded-md bg-gray-100 text-gray-500 text-lg">
            Loading available dates...
          </div>
        ) : (
          <DatePicker
            id="preferredDate"
            selected={
              shortLeadFormPreferredDate
                ? parseLocalDate(shortLeadFormPreferredDate)
                : null
            }
            onChange={(date) =>
              setShortLeadFormPreferredDate(date ? formatLocalDate(date) : null)
            }
            minDate={getMinDate()}
            filterDate={filterDate}
            excludeDateIntervals={getExcludedDateIntervals()}
            dateFormat="MMMM d, yyyy"
            placeholderText="Select a date"
            className="w-full max-w-md px-4 py-3 mb-0 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-coit-red focus:border-transparent text-lg"
            disabled={holidaysLoading}
            required
          />
        )}
      </div>

      <ServicesCheckBoxList services={currentFranchise?.services || {}} />

      {/* What services do you need text area */}
      <div>
        <label
          htmlFor="servicesNeeded"
          className="block text-lg font-semibold mb-2"
        >
          Additional information
        </label>
        <textarea
          id="servicesNeeded"
          value={shortLeadFormNote || ""}
          onChange={(e) => setShortLeadFormNote(e.target.value)}
          placeholder="Please provide any additional information"
          rows={3}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-coit-red focus:border-transparent text-base resize-y"
          required
        />
      </div>
    </div>
  );
}
